import React from "react";
import Storyblok, { version } from "../../lib/storyblok-client";
import { relationsToResolve } from "@/constants/general";
import Components from "../../components/components";
import { GetCoachCards, getCoachCards } from "@/lib/storyblok-gapi";
import { getSeoFromStory } from "@/lib/seo";
import { useStoryblok } from "../../helpers/storyblok-hook";
import { isProduction } from "../../helpers/environment";
import { Story } from "@/types/storyblok";

interface Props {
  story?: Story;
  coaches: GetCoachCards;
  headCoaches: GetCoachCards;
}

export default function CoachesIndex({ story, coaches, headCoaches }: Props) {
  story = useStoryblok(story, !isProduction);

  return (
    story &&
    React.createElement(Components(story?.content?.component), {
      key: story?.uuid,
      content: story?.content,
      published: story.published_at,
      coaches,
      headCoaches,
    })
  );
}

export async function getStaticProps() {
  try {
    const {
      data: { story },
    } = await Storyblok.get(`cdn/stories/coaches`, {
      resolve_relations: relationsToResolve,
      version,
    });

    const coachItems = story?.content?.coaches_to_show || [];
    const headCoachItems = story?.content?.head_coaches || [];
    const isDefinedUuid = (uuid: string | undefined): uuid is string =>
      Boolean(uuid);
    const isCoachCard = (
      coach: GetCoachCards[number] | undefined
    ): coach is GetCoachCards[number] => Boolean(coach);
    const coachIds = coachItems
      .map((coachItem: { coach?: string }) => coachItem?.coach)
      .filter(isDefinedUuid);
    const headCoachIds = headCoachItems
      .map((coachItem: { coach?: string }) => coachItem?.coach)
      .filter(isDefinedUuid);
    const uniqueCoachIds = Array.from(new Set([...headCoachIds, ...coachIds]));
    const allCoaches = uniqueCoachIds.length
      ? await getCoachCards({ uuids: uniqueCoachIds.join(",") })
      : [];
    const coachByUuid = new Map(
      allCoaches.map((coach) => [coach.uuid, coach] as const)
    );
    const headCoachIdSet = new Set(headCoachIds);
    const headCoaches = headCoachIds
      .map((uuid: string) => coachByUuid.get(uuid))
      .filter(isCoachCard);
    const coaches = coachIds
      .filter((uuid: string) => !headCoachIdSet.has(uuid))
      .map((uuid: string) => coachByUuid.get(uuid))
      .filter(isCoachCard);

    return {
      props: {
        story,
        coaches,
        headCoaches,
        seo: await getSeoFromStory({ story }),
      },
    };
  } catch (error) {
    console.log(error);
    return {
      notFound: true,
    };
  }
}
